// client/src/components/LocationSearchBox.js - Mit OpenStreetMap Nominatim statt Google Maps
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { searchLocations, reverseGeocode } from '../services/mapService';
// import './LocationSearchBox.css'; // Alte CSS entfernen
import { AutoComplete, Input, Button, Spin, Alert, Space } from 'antd';
import { SearchOutlined, EnvironmentOutlined, CloseCircleOutlined, LoadingOutlined } from '@ant-design/icons';

const LocationSearchBox = ({ initialValue = '', onSearch, isLoading: parentIsLoading = false }) => {
    const [query, setQuery] = useState(initialValue);
    const [options, setOptions] = useState([]); // Optionen für AutoComplete
    const [isSearching, setIsSearching] = useState(false); // Lokaler Ladezustand für Vorschläge
    const [isGettingLocation, setIsGettingLocation] = useState(false);
    const [locationError, setLocationError] = useState(null);
    const debounceTimeout = useRef(null);

    // Effekt, um den externen Ladezustand zu berücksichtigen
    const isLoading = parentIsLoading || isSearching || isGettingLocation;

    // Debounced function to fetch suggestions
    const fetchSuggestions = useCallback(async (searchText) => {
        if (!searchText || searchText.length < 2) {
            setOptions([]);
            setIsSearching(false); // Ensure loading state is reset
            return;
        }

        setIsSearching(true);
        setLocationError(null);
        try {
            const predictions = await searchLocations(searchText);
            const formattedOptions = predictions?.map(p => ({
                value: p.description, // Der Wert, der bei Auswahl gesetzt wird
                label: (
                    <Space>
                        <EnvironmentOutlined style={{ color: '#888' }}/>
                        {p.description}
                    </Space>
                 ), // Was angezeigt wird
                key: p.id
            })) || [];
            setOptions(formattedOptions);
        } catch (error) {
            console.error('Fehler beim Abrufen der Ortsvorschläge:', error);
            setOptions([]); // Fehler beim Abrufen
        } finally {
            setIsSearching(false);
        }
    }, []); // Empty dependency array, relies on passed searchText

    // Handler for input changes - triggers state update and debounced search
    const handleInputChange = useCallback((value) => {
        const newQuery = value || '';
        setQuery(newQuery); // Update input value state immediately
        setLocationError(null); // Clear previous errors

        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        // Only fetch suggestions if the input is not empty and long enough
        if (newQuery && newQuery.length >= 2) {
             debounceTimeout.current = setTimeout(() => {
                // Pass the current query value at the time the timeout executes
                fetchSuggestions(newQuery);
            }, 300); // 300ms debounce
        } else {
            // Clear suggestions immediately if input is too short or cleared
            setOptions([]);
            setIsSearching(false); // Reset loading state if search is cancelled
        }

    }, [fetchSuggestions]); // Depend on fetchSuggestions callback

    // Wenn ein Vorschlag ausgewählt wird
    const handleSelect = (value) => {
        setQuery(value);
        setOptions([]); // Vorschläge schließen
        onSearch(value); // Suche im Parent auslösen
    };

     // Wenn Enter gedrückt oder Suchbutton geklickt wird
     const triggerSearch = () => {
        if (query.trim()) {
            onSearch(query.trim());
            setOptions([]); // Vorschläge schließen
        }
    };

    // Aktuellen Standort verwenden
    const handleUseLocation = useCallback(async () => {
        setIsGettingLocation(true);
        setLocationError(null);
        setOptions([]); // Vorschläge schließen

        try {
            if (!navigator.geolocation) {
                throw new Error('Geolokalisierung wird von Ihrem Browser nicht unterstützt');
            }
            const position = await new Promise((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(resolve, reject, {
                    enableHighAccuracy: true, timeout: 10000, maximumAge: 0
                });
            });
            const { latitude, longitude } = position.coords;
            const addressData = await reverseGeocode(latitude, longitude);

            if (addressData && addressData.address) {
                const city = addressData.address.city || addressData.address.town || addressData.address.village || addressData.address.county || null;
                if (city) {
                    setQuery(city);
                    onSearch(city); // Suche direkt auslösen
                } else {
                    throw new Error('Standort konnte keiner Stadt zugeordnet werden.');
                }
            } else {
                throw new Error('Adresse konnte nicht ermittelt werden.');
            }
        } catch (error) {
            console.error('Fehler beim Abrufen des Standorts oder Geocoding:', error);
            let specificError = 'Standort konnte nicht ermittelt werden.';
            if (error && typeof error.code === 'number') { /* GeolocationPositionError */ }
            else if (error.message) {
                specificError = error.message;
            }
            setLocationError(specificError);
        } finally {
            setIsGettingLocation(false);
        }
    }, [onSearch]);

     // Cleanup debounce timer
     useEffect(() => {
        return () => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
    }, []);

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <Space.Compact style={{ width: '100%' }}>
                 <AutoComplete
                    value={query}
                     options={options}
                    style={{ width: '100%' }}
                     // onSearch={handleSearch} // <-- REMOVED THIS PROP
                    onSelect={handleSelect}
                    placeholder="Stadt oder Postleitzahl eingeben..."
                     disabled={parentIsLoading || isGettingLocation}
                    allowClear={{ clearIcon: <CloseCircleOutlined /> }}
                     onChange={handleInputChange} // <-- USE NEW HANDLER HERE
                    onClear={() => handleInputChange('')} // Use the same handler for consistency
                 >
                     {/* Optional: Add Input wrapper if suffix is needed for loading indicator */}
                     {/* <Input suffix={isSearching ? <LoadingOutlined /> : null} /> */}
                </AutoComplete>
                <Button
                    type="primary"
                     icon={<SearchOutlined />}
                     onClick={triggerSearch}
                     loading={parentIsLoading} // Nur den Parent-Loading-Zustand anzeigen
                     disabled={isLoading}
                 />
             </Space.Compact>

             <Button
                 icon={<EnvironmentOutlined />}
                 onClick={handleUseLocation}
                 loading={isGettingLocation}
                 disabled={parentIsLoading || isGettingLocation}
                 block // Nimmt volle Breite ein
                 style={{ marginTop: '8px' }}
            >
                Aktuellen Standort verwenden
             </Button>

             {locationError && (
                <Alert message={locationError} type="error" showIcon style={{ marginTop: '10px' }} />
            )}
         </Space>
    );
};

export default LocationSearchBox;